import type { AxiosError } from 'axios';
import { getUpdateAuthenticationTokenEndpoint } from '@/api/helpers/get-update-authentication-token-endpoint';
import { createApiAxios } from '@/utilities/axios/api-axios';
import { HEADERS } from '@/utilities/constants/metadata';
import { useStore } from '@/store/useStore';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';

const UPDATE_AUTHENTICATION_TOKEN_TIMEOUT_MS = 5000;

/** Collapses AW/UPDATE_TOKENS + subscribeSession double-fires (e.g. after RG). */
export const AUTH_TOKEN_UPDATE_DEDUPE_MS = 5000;

type UpdateAuthenticationTokenResponse = {
  data?: Record<string, unknown>;
  error?: Record<string, unknown>;
};

let lastSuccessfulAuthUpdateAt = 0;
let inFlightAuthUpdate: Promise<boolean> | null = null;

export const resetAuthenticationTokenUpdateDedupeForTests = (): void => {
  lastSuccessfulAuthUpdateAt = 0;
  inFlightAuthUpdate = null;
};

const skipAuthUpdate = (reason: 'same-token' | 'in-flight' | 'cooldown') => {
  Logger.logRumAction({
    name: '[GW][AUTH_UPDATE] Skipped',
    useCase: DdEventCategory.UpdateAuthentication,
    eventType: DdEventType.Info,
    context: { reason },
  });
};

const patchAuthenticationToken = async (
  authToken: string,
): Promise<boolean> => {
  const { latestAuthenticationTokenUpdated, gameFetchSuccess } =
    useStore.getState().gameStatus;
  const { userId } = useStore.getState().userSessionContext;
  const { glsRequestId, launchGeoProduct } = useStore.getState().gameContext;
  const endpoint = getUpdateAuthenticationTokenEndpoint();

  if (!gameFetchSuccess) {
    Logger.logRumAction({
      name: '[GW][AUTH_UPDATE] Skipped - GLS fetch not complete',
      useCase: DdEventCategory.UpdateAuthentication,
      eventType: DdEventType.Info,
    });
    return false;
  }

  if (latestAuthenticationTokenUpdated === authToken) {
    Logger.logRumAction({
      name: '[GW][AUTH_UPDATE] Skipped',
      useCase: DdEventCategory.UpdateAuthentication,
      eventType: DdEventType.Info,
    });
    return false;
  }

  if (!authToken || !userId || !glsRequestId || !endpoint) {
    return false;
  }

  const { product: runtimeProduct } = useStore.getState().runtime;
  const product =
    launchGeoProduct === 'CASINO' || launchGeoProduct === 'POKER'
      ? launchGeoProduct
      : (runtimeProduct || '').toUpperCase();

  if (product !== 'CASINO' && product !== 'POKER') {
    return false;
  }

  const instance = createApiAxios();

  Logger.logRumAction({
    name: '[GW][AUTH_UPDATE] Begin',
    useCase: DdEventCategory.UpdateAuthentication,
    eventType: DdEventType.Info,
  });

  try {
    const { data } = await instance.patch<UpdateAuthenticationTokenResponse>(
      endpoint,
      {
        requestId: glsRequestId,
        userId,
        authToken,
        product,
      },
      {
        timeout: UPDATE_AUTHENTICATION_TOKEN_TIMEOUT_MS,
        headers: { [HEADERS.CACHE_CONTROL]: null },
      },
    );

    if (data?.error) {
      Logger.logRumError({
        name: '[GW][AUTH_UPDATE] Rejected',
        useCase: DdEventCategory.UpdateAuthentication,
        eventType: DdEventType.Error,
        context: {
          requestId: glsRequestId,
          errorCode: data.error.errorCode,
          errorMessage: data.error.message,
        },
      });
      return false;
    }

    useStore
      .getState()
      .setGameStatus({ latestAuthenticationTokenUpdated: authToken });
    lastSuccessfulAuthUpdateAt = Date.now();

    Logger.logRumAction({
      name: '[GW][AUTH_UPDATE] Success',
      useCase: DdEventCategory.UpdateAuthentication,
      eventType: DdEventType.Success,
    });

    return true;
  } catch (error) {
    const axiosError = error as AxiosError<{ errorCode?: string }>;

    Logger.logRumError({
      name: '[GW][AUTH_UPDATE] Error',
      useCase: DdEventCategory.UpdateAuthentication,
      eventType: DdEventType.Error,
      context: {
        response_code: axiosError?.response?.status ?? 0,
        error_message: axiosError?.response?.data?.errorCode,
        error,
      },
    });

    return false;
  }
};

/**
 * Pushes a refreshed auth JWT to FGP (PATCH .../update/authToken).
 *
 * Port of casino-game-client's `updateAuthenticationToken` thunk — game-window
 * owns session refresh in v3, so FGP is updated from the shell instead of
 * posting `GL/AUTHENTICATION_TOKEN_UPDATE` into GC.
 */
export const updateAuthenticationToken = async (
  authToken: string,
): Promise<boolean> => {
  const { latestAuthenticationTokenUpdated } = useStore.getState().gameStatus;

  if (latestAuthenticationTokenUpdated === authToken) {
    skipAuthUpdate('same-token');
    return false;
  }

  if (inFlightAuthUpdate) {
    skipAuthUpdate('in-flight');
    return false;
  }

  const now = Date.now();
  if (
    lastSuccessfulAuthUpdateAt > 0 &&
    now - lastSuccessfulAuthUpdateAt < AUTH_TOKEN_UPDATE_DEDUPE_MS
  ) {
    skipAuthUpdate('cooldown');
    return false;
  }

  inFlightAuthUpdate = patchAuthenticationToken(authToken).finally(() => {
    inFlightAuthUpdate = null;
  });

  return inFlightAuthUpdate;
};
